import React, { useEffect, useCallback, useRef, useState, useMemo, PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SesamLink from 'Common/Links/SesamLink';
import isUndefined from 'lodash/isUndefined';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
  getAvailableActions,
  getSinkDataset,
  getUpstreamPipes,
  getDownstreamPipes,
} from 'Internals/pipes';
import { Flows } from 'Internals/flows';
import { PipeActions } from 'Redux/thunks/pipes.thunks';
import { ModelActions } from 'Redux/thunks/models.thunks';
import { DatasetActions } from 'Redux/thunks/datasets.thunks';
import { toastAdded } from 'Redux/thunks/global.thunks';
import useAsync from '../../../hooks/useAsync';
import useClipboard from '../../../hooks/useClipboard';
import { InternalPipesThunks } from '../../../redux-store/thunks/internalPipes.thunks';

import { entitiesRefreshed } from 'src/events/entitiesRefreshed.event';

import { usePipesTabs } from 'src/hooks/usePipesTabs.hook';
import { useGetCurrentPipe } from 'src/hooks/useGetCurrentPipe.hook';
import { getLineageRelatedPipeBreakdown } from 'src/utils/pipesLineageFilters.utils';

import Button from 'Common/Button/Button';
import { LoadingPanel } from 'Common/LoadingPanel';
import PipeProgressRenderer from 'Common/table-list-renderers/pipe-progress';
import InfoTooltip from 'Common/InfoTooltip/InfoTooltip';
import ErrorBoundary from 'Common/ErrorBoundary/ErrorBoundary';
import { makeUrlParamsFromPath } from 'Internals/graph';
import SesamTextField from 'Common/SesamTextField/SesamTextField';
import PipeActionsMenu from '../../../components/pipe-actions-menu';
import { TabbedNav } from '../../../components/tabbed-nav/TabbedNav.view';
import NotFound from '../../../components/not-found';
import Page from '../../../components/page';
import PageHeader, { PageHeaderGroup, PageHeaderTitle } from '../../../components/page-header';
import PopupAction from '../../../components/action/PopupAction';
import SesamModal from '../../../components/common/SesamModal/SesamModal';
import OutgoingFlowWizard from '../../../components/outgoing-flow-wizard/OutgoingFlowWizard';

import type { RouteComponentProps } from 'react-router';
import type { ApiConf, DownstreamMap, LookupMap, SomeObject } from '../../../types/common.types';
import type { RootState } from '../../../types/state.types';
import type { PipeFields, PipeMap } from '../../../types/pipes.types';
import type { Flow } from '../../../types/flows.types';

import { TestID } from '../../../testID';

import './Pipe.css';

type RefreshFn = (refresh?: Function) => void;

export const PipeContainer: React.FC<PropsWithChildren<RouteComponentProps<{}, any>>> = ({
  children,
  params,
  router,
}) => {
  const registeredRefreshes = useRef<RefreshFn[]>([]);

  const { pipeID } = params;

  const dispatch = useDispatch();

  const [pipesFlows, setPipesFlows] = useState<Flow[]>([]);
  const [customActions, setCustomActions] = useState<unknown[]>([]);
  const [cannotFindObject, setCannotFindObject] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  const subId = useSelector((state: RootState) => state.subscription.id);
  const datasets = useSelector((state: RootState) => state.datasets);
  const pipes = useSelector((state: RootState) => state.pipes);
  const upstreams = useSelector((state: RootState) => state.upstreams);
  const downstreams = useSelector((state: RootState) => state.downstreams);
  const lookups = useSelector((state: RootState) => state.lookups);
  const token = useSelector((state: RootState) => state.subscription.token);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const darkModeActive = useSelector((state: RootState) => state.theme.dark);
  const models = useSelector((state: RootState) => state.models);
  const datasetsLoaded = useSelector((state: RootState) => state.loadStatus.datasetsLoaded);

  const currentPipe = useGetCurrentPipe(pipeID);

  const deleteFn = async (id: string) => dispatch(PipeActions.delete([id]));
  const load = async (id: string) => dispatch(PipeActions.load(id));
  const runPumpOperation = (id: string, operation: string, parameters: SomeObject) =>
    dispatch(PipeActions.runPumpOperation(id, operation, parameters));
  const deleteSinkDataset = async (id: string) => dispatch(DatasetActions.delete(id));
  const showDeleteSinkDatasetToast = () =>
    dispatch(toastAdded({ message: 'Sink dataset deleted', type: 'success' }));
  const partialLoad = async (id: string, fields: PipeFields) =>
    dispatch(PipeActions.partialLoad(id, fields));
  const loadAllModels = async () => dispatch(ModelActions.loadAll());
  const loadDataset = async (datasetID: string) =>
    dispatch(DatasetActions.load(datasetID, true, true));
  const addToast = (message: string) => {
    dispatch(toastAdded({ message, type: 'success' }));
  };
  const loadInternalPipes = (apiConf: ApiConf) =>
    dispatch(InternalPipesThunks.loadInternalPipes(apiConf));

  useClipboard('.dataset-inspector-viewer__entity-header-copy-button', () => {
    addToast('Copied to clipboard!');
  });

  const _onDelete = useCallback((pipeId: string) => {
    deleteFn(pipeId).then(() => router.push(`/subscription/${subId}/pipes`));
  }, []);

  const [onDelete] = useAsync(_onDelete);

  const _onDeleteSinkDataset = useCallback(
    (id: string) => {
      deleteSinkDataset(id).then(showDeleteSinkDatasetToast);
    },
    [deleteSinkDataset, showDeleteSinkDatasetToast]
  );

  const [onDeleteSink] = useAsync(_onDeleteSinkDataset);

  const _refresh = useCallback(() => {
    // Note: this returns immediately, so the loading state when used with
    // useAsync is not correct
    load(pipeID).catch(() => setCannotFindObject(true));
    registeredRefreshes.current.forEach((cb) => cb());

    entitiesRefreshed.emit();
  }, [pipeID]);

  const [refresh] = useAsync(_refresh);

  const [currentDisableComment, setCurrentDisableComment] = useState('');

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    loadAllModels();
  }, []);

  const modelId = useMemo(() => {
    const pipeId = get(currentPipe, '_id');
    if (pipeId && !isEmpty(models)) {
      const model = models.find(
        (curr) =>
          curr['entity_types'] && Object.keys(curr['entity_types']).some((_id) => _id === pipeId)
      );
      if (model) return model['_id'];
    }
  }, [models, currentPipe]);

  const datasetID = get(currentPipe, 'config.effective.sink.dataset');
  /**
   * This is to make sure we are working with the latest version of the dataset.
   */
  useEffect(() => {
    if (datasetsLoaded && datasetID) {
      loadDataset(datasetID).catch((e) => {
        console.log(e);
      });
    }
  }, [datasetsLoaded, datasetID]);

  // to refresh the pipe[runtime] data every 2-seconds
  // This is for removing pipe config warnings automatically wihout needing for a custom refersh
  const pipeRuntimeState = get(currentPipe, 'runtime.state');

  useEffect(() => {
    if (pipeRuntimeState === 'Deploying') {
      const id = setInterval(() => {
        partialLoad(pipeID, ['runtime']);
      }, 2000);

      return () => {
        clearInterval(id);
      };
    }
  }, [pipeRuntimeState]);

  const registerRefresh = useCallback((func: RefreshFn) => {
    registeredRefreshes.current.push(func);
  }, []);

  const unregisterRefresh = useCallback((func: RefreshFn) => {
    registeredRefreshes.current = registeredRefreshes.current.filter((c) => c !== func);
  }, []);

  const getPipesFlows = useCallback(
    (pipeId: string, pipes: PipeMap, downstreams: DownstreamMap, lookups: LookupMap) => {
      Flows.get(pipes, downstreams, lookups).then((flows) => {
        const filtered = flows.filter((flow) => flow.path.includes(pipeId));

        setPipesFlows(filtered);
      });
    },
    []
  );

  function getPipeOperationButtons() {
    const btnFactory = (val: string) => {
      return (
        <Button key={val} onClick={() => runPumpOperation(currentPipe._id, val, {})}>
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </Button>
      );
    };
    const availableActions = getAvailableActions(currentPipe);
    const buttons = [];

    if (availableActions.includes('start')) {
      buttons.push(btnFactory('start'));
    } else if (availableActions.includes('stop')) {
      buttons.push(btnFactory('stop'));
    }

    if (availableActions.includes('enable')) {
      buttons.push(btnFactory('enable'));
    } else if (availableActions.includes('disable')) {
      buttons.push(
        <PopupAction
          key="pop-up-action"
          name="Disable"
          useListItem={false}
          onActionClick={() => {
            const parameters: { comment: null | string } = { comment: null };

            if (currentDisableComment !== '') {
              parameters.comment = currentDisableComment;
            }
            runPumpOperation(currentPipe._id, 'disable', parameters);
          }}
          onClosed={() => setCurrentDisableComment('')}
        >
          {/* @ts-ignore */}
          <SesamTextField
            id="disable"
            value={currentDisableComment}
            onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentDisableComment(ev.target.value)
            }
            helperText="You can add comment describing the reason for disabling these pipes. Leave blank for no comment."
          />
        </PopupAction>
      );
    }

    return buttons;
  }

  const addCustomActions = useCallback((actions: any[]) => {
    setCustomActions(actions);
  }, []);

  const removeCustomActions = useCallback(() => {
    setCustomActions([]);
  }, []);

  useEffect(() => {
    if (currentPipe) {
      getPipesFlows(currentPipe._id, pipes, downstreams, lookups);
    }
  }, [currentPipe, pipes, upstreams, downstreams, lookups]);

  const upstreamPipes = getUpstreamPipes(currentPipe, pipes, upstreams);
  const downstreamPipes = getDownstreamPipes(currentPipe, pipes, downstreams);

  const { upstream, downstream } = useMemo(() => {
    const allPipes = Object.values(pipes).filter((pipe) => pipe.origin !== 'search');

    return getLineageRelatedPipeBreakdown({
      pipeId: currentPipe?._id ?? '',
      pipes: allPipes,
      pipesById: pipes,
      upstreams,
      downstreams,
      lookups,
    });
  }, [currentPipe, pipes, upstreams, downstreams, lookups]);

  useEffect(() => {
    if (token && subUrl) {
      loadInternalPipes({ token, subUrl });
    }
  }, [token, subUrl]);

  let sinkDatasetID = '';
  let sinkDataset;

  if (!isUndefined(datasets)) {
    sinkDataset = getSinkDataset(currentPipe, datasets);
    sinkDatasetID = !isUndefined(sinkDataset) ? sinkDataset._id : '';
  }

  const pipeTabs = usePipesTabs({ subId, pipe: currentPipe, sinkDatasetID });

  if (!currentPipe) {
    return <LoadingPanel />;
  }

  let flowButtonLink = '';
  let flowButtonText = '';

  if (currentPipe) {
    if (pipesFlows.length > 1) {
      flowButtonLink = `/subscription/${subId}/flows/all/?Includes.selected=${currentPipe._id}`;
      flowButtonText = 'Show flows';
    } else if (pipesFlows.length === 1) {
      flowButtonLink = `/subscription/${subId}/flows/flow/dashboard?${makeUrlParamsFromPath(
        pipesFlows[0].path
      )}`;
      flowButtonText = 'Go to flow';
    }

    const description = get(currentPipe, ['config', 'original', 'description']);

    return (
      <ErrorBoundary>
        <Page className="page--pipe">
          {/* @ts-ignore */}
          <PageHeader>
            <PageHeaderTitle>
              <SesamLink to={`/subscription/${subId}/pipes`}>Pipes</SesamLink>
            </PageHeaderTitle>
            {currentPipe && (
              <React.Fragment>
                <PageHeaderTitle style={{ marginRight: '5px' }}>
                  {currentPipe.name || pipeID}
                </PageHeaderTitle>
                {description && (
                  <InfoTooltip info={description} size="medium" style={{ marginRight: '5px' }} />
                )}
              </React.Fragment>
            )}
            {currentPipe && (
              <PageHeaderGroup>
                <PipeActionsMenu
                  actionExecutor={runPumpOperation}
                  onDelete={onDelete}
                  onDeleteSinkDataset={onDeleteSink}
                  pipe={currentPipe}
                  subId={subId}
                  sinkDatasetID={sinkDatasetID}
                  token={token}
                  subUrl={subUrl}
                  flowsLabel={flowButtonText}
                  flowsLink={flowButtonLink}
                  onOpenWizard={() => setShowWizard(true)}
                  onNavigate={(path: string) => router.push(path)}
                  upstreamCount={upstream.totalCount}
                  upstreamDirectCount={upstream.directCount}
                  upstreamHopCount={upstream.hopCount}
                  downstreamCount={downstream.totalCount}
                  downstreamDirectCount={downstream.directCount}
                  downstreamHopCount={downstream.hopCount}
                  addToast={addToast}
                />
              </PageHeaderGroup>
            )}
            {currentPipe && (
              <PageHeaderGroup>
                <PipeProgressRenderer
                  progress={currentPipe?.runtime?.progress}
                  runtime={currentPipe.runtime}
                  lastRunWithDate
                />
                {customActions.length > 0 && customActions.map((c) => c)}
                {currentPipe && modelId && (
                  <SesamLink
                    to={`/subscription/${subId}/browse/entity-types/${modelId}/${currentPipe._id}`}
                    buttonLink={true}
                  >
                    <Button>Sink entity type</Button>
                  </SesamLink>
                )}
                {upstreamPipes.length === 1 && (
                  <SesamLink
                    to={`/subscription/${subId}/pipes/pipe/${upstreamPipes[0]._id}`}
                    buttonLink={true}
                  >
                    <Button>Go upstream</Button>
                  </SesamLink>
                )}
                {downstreamPipes.length === 1 && (
                  <SesamLink
                    to={`/subscription/${subId}/pipes/pipe/${downstreamPipes[0]._id}`}
                    buttonLink={true}
                  >
                    <Button>Go downstream</Button>
                  </SesamLink>
                )}

                {pipesFlows.length > 0 && (
                  <SesamLink to={flowButtonLink} buttonLink={true}>
                    <Button>{flowButtonText}</Button>
                  </SesamLink>
                )}
                {getPipeOperationButtons()}
                <Button
                  theme="primary"
                  onClick={() => refresh()}
                  data-testid={TestID.RefreshEntitiesButton}
                >
                  Refresh
                </Button>
              </PageHeaderGroup>
            )}
          </PageHeader>
          {!cannotFindObject && !currentPipe && <LoadingPanel />}
          {cannotFindObject && (
            <NotFound
              link={`/subscription/${subId}/pipes`}
              linkText="View pipes"
              message="Maybe you misspelled an ID?"
              title="Pipe not found"
            />
          )}
          {!cannotFindObject && currentPipe && (
            <TabbedNav nav={pipeTabs}>
              {!cannotFindObject &&
                // @ts-ignore
                React.cloneElement(children, {
                  registerRefresh: registerRefresh,
                  unregisterRefresh: unregisterRefresh,
                  addCustomActions: addCustomActions,
                  removeCustomActions: removeCustomActions,
                })}
            </TabbedNav>
          )}
          {sinkDataset && (
            //  @ts-ignore
            <SesamModal
              className="simple-dialog dataset__wizard-modal"
              isOpen={showWizard}
              onRequestClose={() => setShowWizard(false)}
              darkModeActive={darkModeActive}
            >
              <OutgoingFlowWizard dataset={sinkDataset} />
            </SesamModal>
          )}
        </Page>
      </ErrorBoundary>
    );
  }
};
